import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaCircleCheck, FaCircleExclamation } from 'react-icons/fa6';
import { CSVFileEntry, ZeitschritteData } from '../../../../types/common';
import { useCSVFiles } from '../CSVFileUploader/hooks';
import { CSVFileEntryCard } from '../CSVFileUploader/CSVFileEntryCard';
import FileMetadataTable from '../FileMetadataTable';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface OutputDataUploaderProps {
  onFilesChange?: (files: CSVFileEntry[]) => void;
  sessionId?: string;
  zeitschritte?: ZeitschritteData;
  globalDuplicateBezeichnungIds?: Set<string>;
  onUploadComplete?: () => void;
}

/**
 * Output Data Uploader component
 * Reuses CSVFileUploader infrastructure with:
 * - fileType='output' for filtering and creating entries
 * - colorTheme='green' for visual distinction
 *
 * Structure:
 * - useCSVFiles hook: state management + CRUD operations (shared)
 * - CSVFileEntryCard: composition of Header + 4 Sections (shared, green theme)
 */
export const OutputDataUploader: React.FC<OutputDataUploaderProps> = ({ onFilesChange, sessionId, zeitschritte, globalDuplicateBezeichnungIds, onUploadComplete }) => {
  const { t } = useTranslation('training');

  // Main state and operations from custom hook (fileType='output' for output files)
  // Uses zeitschritte.ausgabe (MTS.O_N) for N calculation
  const csvFiles = useCSVFiles(sessionId, onFilesChange, 'output', zeitschritte, onUploadComplete);

  return (
    <div className="rounded-xl border border-border bg-card overflow-visible shadow-sm">
      <div className="flex items-center justify-center gap-1.5 px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight">
          {t('outputDataUploader.title')}
        </h2>
        <InfoTooltip text={t('tooltips.config.ausgabedaten')} width="w-80" />
        {csvFiles.isLoading && (
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <FaSpinner className="animate-spin text-[10px]" />
            <span>{t('csvFileUploader.loading') || 'Loading...'}</span>
          </div>
        )}
      </div>

      <div className="p-4 sm:p-6">
        {/* Messages */}
        {csvFiles.successMessage && (
          <div className="flex items-center gap-2.5 px-4 py-3 mb-4 rounded-lg bg-emerald-50 border border-emerald-200/60">
            <FaCircleCheck className="text-emerald-500 text-sm flex-shrink-0" />
            <p className="text-xs sm:text-sm text-emerald-700">
              {t('csvFileUploader.messages.success')}
            </p>
          </div>
        )}
        {csvFiles.errorMessages.length > 0 && (
          <div className="flex items-start gap-2.5 px-4 py-3 mb-4 rounded-lg bg-red-50 border border-red-200/60">
            <FaCircleExclamation className="text-red-500 text-sm flex-shrink-0 mt-0.5" />
            <ul className="space-y-0.5">
              {csvFiles.errorMessages.map((error, index) => (
                <li key={index} className="text-xs sm:text-sm text-red-700">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* File Entry Cards */}
        <div role="list" className="space-y-3 flex flex-col items-center">
          {csvFiles.entries.map((entry, index) => (
            <div role="listitem" key={entry.id} className="w-full flex justify-center">
              <CSVFileEntryCard
                entry={entry}
                indexLabel={`OUT${index + 1}`}
                onInputChange={(field: keyof CSVFileEntry, value: string) => csvFiles.handleInputChange(entry.id, field, value)}
                onRadioChange={(value, field) => csvFiles.handleRadioChange(entry.id, value, field)}
                onFileChange={(e) => csvFiles.handleFileChange(entry.id, e)}
                onRemove={() => csvFiles.removeEntry(entry.id)}
                onUpload={() => csvFiles.uploadEntry(entry.id)}
                onSave={() => csvFiles.saveEntry(entry.id)}
                isUploading={csvFiles.isLoading}
                isDirty={csvFiles.isEntryDirty(entry.id)}
                colorTheme="green"
                hasDuplicateBezeichnung={globalDuplicateBezeichnungIds?.has(entry.id) ?? false}
                sessionId={sessionId}
              />
            </div>
          ))}
        </div>

        <FileMetadataTable entries={csvFiles.entries} colorTheme="green" />
      </div>
    </div>
  );
};

export default OutputDataUploader;
